import React, { useEffect, useState } from "react";
import {
  listarClientesZenTag,
  bloquearClienteZenTag,
  desbloquearClienteZenTag,
} from "../services/api";

import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Plus,
  ShieldOff,
  Unlock,
  Edit,
  Search,
  Users,
  KeyRound,
} from "lucide-react";
import BlockModal from "./common/BlockModal";
import ZentagAuthModal from "./common/ZentagAuthModal";

const DEFAULT_BLOCK_MESSAGE =
  "Entre em contato com o departamento financeiro";

const ListaZenTag = ({ token }) => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const qp = (k, def = "") => searchParams.get(k) ?? def;

  const [clientes, setClientes] = useState([]);

  // filtros (URL) - SEM revendaId
  const [busca, setBusca] = useState(qp("search", ""));
  const [ativo, setAtivo] = useState(qp("ativo", "all")); // all | 1 | 0

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedAuthCtx, setSelectedAuthCtx] = useState(null);

  const openUsuariosZentag = (licId) =>
    navigate(`/licenciamento/zentag/${licId}/usuarios`);

  const openAuthModal = (cliente) => {
    setSelectedAuthCtx({
      licId: cliente.id,
      estabelecimentoNome:
        cliente.nomeFantasia ||
        cliente.clienteNome ||
        cliente.clienteRazaoSocial ||
        "-",
      slug: cliente.slug || "",
      estabelecimentoId:
        cliente.zenTagEstabelecimentoId ||
        cliente.ZenTagEstabelecimentoId ||
        null,
    });
    setShowAuthModal(true);
  };

  // modal bloqueio
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedClientToBlock, setSelectedClientToBlock] = useState(null);

  const fetchClientes = async () => {
    try {
      const resp = await listarClientesZenTag(token, {
        search: busca || undefined,
        // revendaId removido
        ativo: ativo === "all" ? undefined : ativo, // "1" ou "0"
      });

      setClientes(resp.data || []);
    } catch (e) {
      console.error("Erro ao listar clientes ZenTag:", e);
      setClientes([]);
    }
  };

  // recarrega lista quando filtros mudarem
  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, busca, ativo]);

  // sincroniza filtros na URL (SEM revendaId)
  useEffect(() => {
    const p = new URLSearchParams(searchParams);

    if (busca) p.set("search", busca);
    else p.delete("search");

    if (ativo && ativo !== "all") p.set("ativo", ativo);
    else p.delete("ativo");

    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, ativo]);

  const handleEditar = (id) => navigate(`/licenciamento/zentag/editar/${id}`);

  const handleToggleBlock = async (id, isAtivo) => {
    if (isAtivo) {
      setSelectedClientToBlock(id);
      setShowBlockModal(true);
      return;
    }

    if (!window.confirm("Tem certeza que deseja desbloquear este cliente?"))
      return;

    try {
      await desbloquearClienteZenTag(id, token);
      alert("Cliente desbloqueado com sucesso!");
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao desbloquear cliente:", error);
      alert("Erro ao desbloquear cliente.");
    }
  };

  const handleConfirmBlock = async (message) => {
    try {
      await bloquearClienteZenTag(
        selectedClientToBlock,
        { MensagemBloqueio: message },
        token
      );

      alert("Cliente bloqueado com sucesso!");
      setShowBlockModal(false);
      setSelectedClientToBlock(null);
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao bloquear cliente:", error);
      alert("Erro ao bloquear cliente.");
    }
  };

  return (
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Licenças ZenTag
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            Filtros: Status (bloqueado = ativo=false)
          </p>
        </div>

        <button
          className="
            inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm transition
            bg-blue-600 text-white hover:bg-blue-500
          "
          onClick={() => navigate("/licenciamento/zentag/novo")}
        >
          <Plus size={18} className="mr-2" />
          Novo ZenTag
        </button>
      </div>

      {/* filtros */}
      <div className="rounded-2xl p-4 mb-6 zen-card dark:bg-white/5 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* busca */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Buscar
            </label>

            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 pointer-events-none"
                size={18}
              />
              <input
                type="text"
                placeholder="Cliente, slug, CNPJ..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>
          </div>

          {/* status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Status
            </label>
            <select
              value={ativo}
              onChange={(e) => setAtivo(e.target.value)}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
            >
              <option value="all">Todos</option>
              <option value="1">Ativos</option>
              <option value="0">Bloqueados</option>
            </select>
          </div>
        </div>
      </div>

      {/* tabela */}
      <div className="overflow-x-auto">
        <div className="min-w-full rounded-2xl zen-card dark:bg-white/5 dark:border-white/10 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Cliente
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  CNPJ
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Slug
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Ativo
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Ações
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-700 dark:text-slate-200">
              {clientes.map((cliente) => (
                <tr
                  key={cliente.id}
                  className="
                    border-t border-gray-200 hover:bg-gray-50 transition
                    dark:border-white/10 dark:hover:bg-white/5
                  "
                >
                  <td className="py-3 px-4">
                    {cliente.nomeFantasia || cliente.clienteNome || "-"}
                  </td>
                  <td className="py-3 px-4">{cliente.clienteCnpj || "-"}</td>
                  <td className="py-3 px-4">{cliente.slug || "-"}</td>

                  <td className="text-center py-3 px-4">
                    {cliente.ativo ? (
                      <CheckCircle className="text-green-500 inline" size={20} />
                    ) : (
                      <XCircle className="text-red-500 inline" size={20} />
                    )}
                  </td>

                  <td className="text-center py-3 px-4">
                    <div className="inline-flex items-center gap-1">
                      <button
                        className="
                          p-2 rounded-full transition
                          text-slate-700 hover:bg-slate-100
                          dark:text-slate-200 dark:hover:bg-white/10
                        "
                        onClick={() => openUsuariosZentag(cliente.id)}
                        title="Usuários do ZenTag"
                      >
                        <Users size={18} />
                      </button>

                      <button
                        className="
                          p-2 rounded-full transition
                          text-amber-700 hover:bg-amber-100
                          dark:text-amber-300 dark:hover:bg-amber-500/10
                        "
                        onClick={() => openAuthModal(cliente)}
                        title="Autenticador (ClientId/Secret)"
                      >
                        <KeyRound size={18} />
                      </button>

                      <button
                        className="
                          p-2 rounded-full transition
                          text-blue-600 hover:bg-blue-100
                          dark:text-blue-300 dark:hover:bg-blue-500/10
                        "
                        onClick={() => handleEditar(cliente.id)}
                        title="Editar"
                      >
                        <Edit size={18} />
                      </button>

                      <button
                        className={`
                          p-2 rounded-full transition
                          ${
                            cliente.ativo
                              ? "text-red-600 hover:bg-red-100 dark:hover:bg-red-500/10"
                              : "text-green-600 hover:bg-green-100 dark:hover:bg-green-500/10"
                          }
                        `}
                        onClick={() =>
                          handleToggleBlock(cliente.id, cliente.ativo)
                        }
                        title={cliente.ativo ? "Bloquear" : "Desbloquear"}
                      >
                        {cliente.ativo ? (
                          <ShieldOff size={18} />
                        ) : (
                          <Unlock size={18} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {clientes.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-10 text-center text-gray-500 dark:text-slate-300/80"
                  >
                    Nenhuma licença ZenTag encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BlockModal
        show={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        onConfirm={handleConfirmBlock}
        defaultMessage={DEFAULT_BLOCK_MESSAGE}
      />

      <ZentagAuthModal
        show={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        token={token}
        licId={selectedAuthCtx?.licId}
        estabelecimentoNome={selectedAuthCtx?.estabelecimentoNome}
        slug={selectedAuthCtx?.slug}
        estabelecimentoIdFromLic={selectedAuthCtx?.estabelecimentoId}
      />
    </div>
  );
};

export default ListaZenTag;
